import React, { useState } from 'react';
import { ParsedSlotRow, ParsedPatternRow } from '../utils/excelParser';
import { ValidationError, getRowErrors, rowHasErrors } from '../utils/excelValidator';

const DAY_NAMES: Record<number, string> = {
  1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat',
};

interface ImportPreviewTableProps {
  rows: (ParsedSlotRow | ParsedPatternRow)[];
  errors: ValidationError[];
  importType: 'slots' | 'pattern';
}

type FilterMode = 'all' | 'valid' | 'errors';

const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({ rows, errors, importType }) => {
  const [filter, setFilter] = useState<FilterMode>('all');

  const validCount = rows.filter((_, i) => !rowHasErrors(errors, i)).length;
  const errorCount = rows.length - validCount;

  const filteredIndices = rows
    .map((_, i) => i)
    .filter((i) => {
      if (filter === 'valid') return !rowHasErrors(errors, i);
      if (filter === 'errors') return rowHasErrors(errors, i);
      return true;
    });

  return (
    <div className="import-preview">
      <div className="import-summary-bar">
        <span className="import-summary-total">{rows.length} rows parsed</span>
        <span className="import-summary-valid">{validCount} valid</span>
        {errorCount > 0 && <span className="import-summary-errors">{errorCount} with errors</span>}
      </div>

      <div className="import-filter-tabs">
        <button
          className={`import-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({rows.length})
        </button>
        <button
          className={`import-filter-btn ${filter === 'valid' ? 'active' : ''}`}
          onClick={() => setFilter('valid')}
        >
          Valid ({validCount})
        </button>
        {errorCount > 0 && (
          <button
            className={`import-filter-btn ${filter === 'errors' ? 'active' : ''}`}
            onClick={() => setFilter('errors')}
          >
            Errors ({errorCount})
          </button>
        )}
      </div>

      <div className="import-table-wrapper">
        <table className="import-preview-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              {importType === 'slots' ? <th>Date</th> : <th>Day</th>}
              <th>Start</th>
              <th>End</th>
              <th>Instructor</th>
              <th>Topic</th>
              {importType === 'slots' && <th>Batches</th>}
              {importType === 'slots' && <th>Online</th>}
              <th>Agenda</th>
            </tr>
          </thead>
          <tbody>
            {filteredIndices.map((i) => {
              const row = rows[i];
              const hasError = rowHasErrors(errors, i);
              const rowErrors = getRowErrors(errors, i);

              return (
                <React.Fragment key={i}>
                  <tr className={hasError ? 'import-row-error' : 'import-row-valid'}>
                    <td>{i + 1}</td>
                    <td>{hasError ? '\u2717' : '\u2713'}</td>
                    {importType === 'slots' ? (
                      <td>{(row as ParsedSlotRow).date || '-'}</td>
                    ) : (
                      <td>{DAY_NAMES[(row as ParsedPatternRow).dayOfWeek] || (row as ParsedPatternRow).dayOfWeek || '-'}</td>
                    )}
                    <td>{row.startTime || '-'}</td>
                    <td>{row.endTime || '-'}</td>
                    <td>
                      {row.instructorId
                        ? `ID: ${row.instructorId}`
                        : row.instructorName || '-'}
                    </td>
                    <td>
                      {row.topicMasterId
                        ? `ID: ${row.topicMasterId}`
                        : row.topicName || '-'}
                    </td>
                    {importType === 'slots' && (
                      <td>{(row as ParsedSlotRow).batches || '-'}</td>
                    )}
                    {importType === 'slots' && (
                      <td>{(row as ParsedSlotRow).isOnline ? '✓' : '-'}</td>
                    )}
                    <td>{row.agenda || '-'}</td>
                  </tr>
                  {hasError && (
                    <tr className="import-error-detail">
                      <td colSpan={importType === 'slots' ? 10 : 8}>
                        {rowErrors.map((err, j) => (
                          <span key={j} className="import-error-msg">
                            {err.field ? `${err.field}: ` : ''}{err.message}
                          </span>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filteredIndices.length === 0 && (
              <tr>
                <td colSpan={importType === 'slots' ? 10 : 8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-faint)' }}>
                  No rows to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportPreviewTable;
